import { useEffect, useState } from 'react';
import { get_deployment, usePost, useRoboScreener } from '../API';

export function useRoboScreenerAPI(props: {
  manualOnly?: boolean,
  /**
   * @param allApplicants if `true`, the changes will apply to
   * all applicants in the program. Only available to admins.
   */
  allApplicants?: boolean,
  uid?: string, 
  uids?: string[],
  limit?: number,
  debugKeys?: Record<string, string>,
  computeOptions?: {
    forceComputeFields?: string[],
    customClockTimestamp?: string
  }
}) {

  const compute = useRoboScreener('/compute_and_save');
  const getAllApplicants = usePost('/applicants/get_all');

  const [queuedApplicants, setApplicants] = useState<any[]>([]);
  const [updates, setUpdates] = useState({} as Record<string, Record<string,string>>);
  const [computing, setComputing] = useState(false);

  const doCompute = async (overwrite?: boolean, saveUpdates?: boolean, params?: {
    onlySaveAffected?: boolean,
    receiveProgressUpdate?: (requestsCompleted: number, totalRequests: number) => void,
  }) => {
    const allUpdates: Record<string, Record<string, string>> = {};
    const localUpdates = localStorage.getItem('rs-dry-updates');

    if (localUpdates && localUpdates.length > 0) {
      Object.assign(allUpdates, JSON.parse(localUpdates));
    }

    setComputing(true);
       
    const applicants: { 
      uid: string, 
      stage?: string,
      hidden?: boolean | null,
    }[] = [];

    if (!saveUpdates) {
      if (props.allApplicants) {               
        const applicantResponse = await getAllApplicants({});
        applicants.push(...applicantResponse);
      } else if (props.uid) {
        applicants.push({ uid: props.uid })
      } else if (props.uids && props.uids.length > 0) {
        applicants.push( ...props.uids.map(uid => ({ uid })) );
        if (!props.limit) props.limit = applicants.length;
      }
      setApplicants(applicants);
    } else {
      if (params?.onlySaveAffected) {
        for (const uid in updates) {
          if (Object.keys(updates[uid] || {}).length > 0) {
            applicants.push({ uid });
          }
        }
      } else {
        applicants.push(...queuedApplicants);
      }
    }

    let index = -1;
    const numberOfRequests = Math.min(applicants.length, props.limit ?? Infinity);
    const reportProgressUpdate = () => params?.receiveProgressUpdate?.(index + 1, numberOfRequests);

    reportProgressUpdate();
    for (const applicant of applicants) {
      index++;
   
      if (applicant.hidden) {
        reportProgressUpdate();
        continue;
      };
            
      if (['Unreachable','Ineligible'].indexOf(applicant.stage || 'asdfsdf') !== -1) {
        reportProgressUpdate();
        continue;
      };

      if (props.limit && index >= props.limit) break;
      if (allUpdates[applicant.uid] && !overwrite) {
        reportProgressUpdate();
        continue;
      };

      const computeParams = {
        deploymentKey: get_deployment(),
        uid: applicant.uid,
        debugKeys: (props.debugKeys || {}),
        dryRunOnly: saveUpdates ? false : true, // very important.
        changedKeys: {},
        computeOptions: {
          ...(props.computeOptions?.forceComputeFields && { forceComputeFields: props.computeOptions.forceComputeFields }),
          ...(props.computeOptions?.customClockTimestamp && { customClockTimestamp: props.computeOptions.customClockTimestamp }),
        }
      }

      // stagger updates
      const updates = await compute(computeParams);
      if (updates && 'value' in updates) {
        allUpdates[applicant.uid] = updates.value;
      }

      reportProgressUpdate();
    }
    console.debug("Setting local storage to allUpdates for uids: ", Object.keys(allUpdates));
    localStorage.setItem('rs-dry-updates', JSON.stringify(allUpdates));
    setUpdates(allUpdates);
    setComputing(false);
  };

  // Compute once on page load
  useEffect(() => {
    // overwrite if a uid is provided, otherwise don't overwrite local storage.
    if (!props.manualOnly) {
      doCompute(props.uid ? true : false, false);
    }
  }, []);

  return { 
    applicantUpdates: updates, 
    computing,
    refreshRS: async (params?: {
      receiveProgressUpdate?: (requestsCompleted: number, totalRequests: number) => void,
    }) => { localStorage.removeItem('rs-dry-updates'); await doCompute(true, false, params) },
    saveUpdates: async (params?: {
      onlySaveAffected?: boolean,
      receiveProgressUpdate?: (requestsCompleted: number, totalRequests: number) => void,
    }) => { 
      // Only dry compute again if there is no face being forced
      // Recomputing dry with _face forces the DetectFaces library to delete the face from
      //   Rekognition indexes. So let's not do that!
      const shouldDryCompute = (props.computeOptions?.forceComputeFields || []).filter(f => f.includes('_face')).length === 0;

      // If we're dry computing after the initial compute then we'll be processing each applicant twice.
      // Since the user is expecting to see a progress bar equal to the number of applicants they've submitted,
      // showing each applicant's progress twice could be confusing. Thus we artificially reduce the number
      // of requests completed by half so that once we've finished the first pass we'll show the progress as
      // half of the applicants processed and once we've finished the second pass we'll show the progress as
      // all of the applicants processed.
      const receiveProgressUpdate = (requestsCompleted: number, totalRequests: number, isDryCompute: boolean) => {
        if (!shouldDryCompute) {
          params?.receiveProgressUpdate?.(requestsCompleted, totalRequests);
        } else if (!isDryCompute) {
          const artificialRequestsCompleted = Math.floor(requestsCompleted / 2);
          params?.receiveProgressUpdate?.(artificialRequestsCompleted, totalRequests);
        } else {
          const artificialRequestsCompleted = Math.floor((requestsCompleted + totalRequests) / 2);
          params?.receiveProgressUpdate?.(artificialRequestsCompleted, totalRequests);
        }
      };

      await doCompute(true, true, {
        ...params,
        receiveProgressUpdate: (requestsCompleted, totalRequests) =>
          receiveProgressUpdate(requestsCompleted, totalRequests, false),
      }); 

      if (shouldDryCompute) 
        await doCompute(true, false, {
          receiveProgressUpdate: (requestsCompleted, totalRequests) =>
            receiveProgressUpdate(requestsCompleted, totalRequests, true),
        }); 
    },
  }
}
